/**
 * BookingProvider
 *
 * App-wide context that owns the BookingModal so any component (Header,
 * Homepage, ContactSalesModal, Contact sidebar, etc.) can trigger a Cal.com
 * booking by calling `useBooking().open(eventType)` — no prop drilling.
 *
 * Mount once near the top of the React tree. See App.tsx.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import BookingModal from './BookingModal';
import type { CalEventType } from '../config/calcom';

interface BookingContextValue {
  /** Open the booking modal with the given event type. */
  open: (eventType?: CalEventType) => void;
  /** Close the booking modal. Rarely needed externally. */
  close: () => void;
  /** Whether the modal is currently open. */
  isOpen: boolean;
}

const BookingContext = createContext<BookingContextValue | null>(null);

export const BookingProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [eventType, setEventType] = useState<CalEventType>('demo');

  const open = useCallback((next: CalEventType = 'demo') => {
    setEventType(next);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const value = useMemo<BookingContextValue>(
    () => ({ open, close, isOpen }),
    [open, close, isOpen]
  );

  return (
    <BookingContext.Provider value={value}>
      {children}
      <BookingModal isOpen={isOpen} onClose={close} eventType={eventType} />
    </BookingContext.Provider>
  );
};

export const useBooking = (): BookingContextValue => {
  const ctx = useContext(BookingContext);
  if (!ctx) {
    throw new Error('useBooking must be used inside <BookingProvider>');
  }
  return ctx;
};

export default BookingProvider;
